import useInertiaData from '@/hooks/useInertiaData';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeType = 'cblocks' | 'cfinance' | 'conquerx' | 'clanguages' | 'clanguages-old';

interface ThemeContextType {
    theme: ThemeType;
    setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const { user, domain_academy } = useInertiaData();
    const [theme, setThemeState] = useState<ThemeType>('conquerx');

    useEffect(() => {
        if (user?.current_academy?.theme) {
            setThemeState(user.current_academy.theme as ThemeType);
        } else if (domain_academy?.theme) {
            setThemeState(domain_academy.theme as ThemeType);
        } else {
            setThemeState('conquerx');
        }
    }, [user?.current_academy, domain_academy]);

    useEffect(() => {
        // Apply theme class to document body
        const body = document.body;
        body.classList.remove('cblocks', 'cfinance', 'conquerx', 'clanguages', 'clanguages-old');
        body.classList.add(theme);
    }, [theme]);

    const setTheme = (newTheme: ThemeType) => {
        setThemeState(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
